import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Mic, Square, Sparkles, Building2, Check, Pause, Play } from "lucide-react";
import { surveyApi } from "@/lib/apiClient";
import type { Answer, AnswerValue, Survey } from "@shared-domain/survey/contract";
import {
  DIALOGUE_END_PHRASE, RECORD_ANSWER_TOOL,
  buildDialogueInstructions, buildDialogueTools, buildSessionUpdateAudioInput,
  coerceToolAnswers, computeSurveyProgress,
} from "@shared-domain/survey/voice";
import { T, hexA } from "./theme";
import { Waveform } from "./Waveform";
import { useRealtimeVoice } from "./useRealtimeVoice";
import { useMediaRecorder } from "./useMediaRecorder";

type Cust = { name: string; no?: string; addr?: string; contact?: string };

const fmt = (s: number): string => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

// スキーマ → 音声ガイド項目 (自由モードで「何を話せばいいか」の手引き)。
function guideItems(survey: Survey): { id: string; label: string; points: string[] }[] {
  return survey.questions.map((q) => {
    const cond = q.phase !== "always" ? "（該当時）" : "";
    const points =
      q.type === "radio" || q.type === "checkbox"
        ? q.options.map((o) => o.label)
        : q.type === "date"
          ? ["実施日"]
          : q.type === "text"
            ? [q.placeholder || "自由に回答"]
            : // 数値評価: 話し手に段階数を伝える (例「1〜5段階評価（1=不満 / 5=満足）」)。
              [
                `${q.scaleMin}〜${q.scaleMax}段階評価` +
                  (q.minLabel || q.maxLabel
                    ? `（${q.scaleMin}=${q.minLabel || "低"} / ${q.scaleMax}=${q.maxLabel || "高"}）`
                    : ""),
              ];
    return { id: q.questionId, label: q.label + cond, points };
  });
}

function CustomerContext({ customer }: { customer: Cust }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E7EDF5", borderRadius: 12, padding: "9px 12px", display: "flex", alignItems: "center", gap: 11 }}>
      <div style={{ width: 34, height: 34, borderRadius: 9, background: "#E7F3F1", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
        <Building2 size={18} color={T.accent} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{customer.name || "対象顧客"}</div>
        {(customer.no || customer.addr) && <div style={{ fontSize: 11, color: T.faint, marginTop: 1, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{customer.no}{customer.no && customer.addr ? " ・ " : ""}{customer.addr}</div>}
      </div>
    </div>
  );
}

/**
 * 音声モード画面。
 * - 対話 (dialog): OpenAI Realtime でチャット形式 (AI 左 / ユーザー 右)。AI が1問ずつ質問。
 * - 自由 (freerec): MediaRecorder で録音 → 停止時に Whisper 文字起こし (ライブ字幕なし・波形+タイマー)。
 * 停止後に AI が調査スキーマへ抽出 → onExtracted で下書きを渡し、次のタップ画面で確認・送信。
 */
export function VoiceAnswer({
  survey, surveyId, mode, customer, prefill, onExtracted, onBack,
}: {
  survey: Survey; surveyId: string; mode: "freerec" | "dialog"; customer: Cust;
  /** 既に確定している回答 (③: 個人顧客のヒアリング相手等)。対話 AI はこれらを質問しない。 */
  prefill?: Record<string, AnswerValue>;
  onExtracted: (answers: Answer[]) => void; onBack: () => void;
}) {
  const isDialog = mode === "dialog";
  // ⑤ 対話中に record_answer ツールで確定した回答 (prefill で初期化)。
  // collected/remaining の算出と、抽出結果の backstop マージに使う。
  const answeredRef = useRef<Map<string, AnswerValue>>(
    new Map(Object.entries(prefill ?? {})),
  );
  // 対話モード: AI が完了フレーズ (お礼+「以上で完了です」) を言ったら自動で終了→確認へ進む。
  // record_answer 呼び出しごとに進捗を tool output で返し「未回答の先頭だけ質問」を強制する。
  const rt = useRealtimeVoice({
    onAutoEnd: () => { void finish(); },
    onToolCall: (name, args) => {
      if (name !== RECORD_ANSWER_TOOL)
        return { output: JSON.stringify({ error: `unknown tool: ${name}` }) };
      for (const a of coerceToolAnswers(survey, args))
        answeredRef.current.set(a.questionId, a.value);
      const progress = computeSurveyProgress(survey, answeredRef.current);
      return {
        output: JSON.stringify({ success: true, ...progress }),
        followUpInstructions: progress.remaining.length
          ? `記録しました。collected の設問は絶対に聞き返さず、回答の復唱もせず、次は「${progress.remaining[0]}」だけを簡潔に質問してください。`
          : `すべての設問が記録済みです。お礼を述べて「${DIALOGUE_END_PHRASE}」で締めくくってください。`,
      };
    },
  });
  const rec = useMediaRecorder();
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [sec, setSec] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  // 自由モードの収録音声を保持し、文字起こし/抽出が失敗しても再試行できるようにする (データロス防止)。
  const pendingAudioRef = useRef<{ base64: string; mimeType: string } | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // モードに応じて対話=Realtime / 自由=録音 の一方だけ起動する。
  // 対話は instructions(④禁止事項+③確定済み項目) + tools(⑤record_answer) を session.update で注入。
  useEffect(() => {
    if (isDialog) {
      const knownAnswers = Object.entries(prefill ?? {}).flatMap(([qid, v]) => {
        const q = survey.questions.find((x) => x.questionId === qid);
        return q && typeof v === "string" && v ? [{ label: q.label, value: v }] : [];
      });
      void rt.start(surveyId, "dialog", {
        instructions: buildDialogueInstructions(survey, { knownAnswers }),
        tools: buildDialogueTools(survey),
        audioInput: buildSessionUpdateAudioInput(survey),
      });
      return () => { void rt.stop(); };
    }
    void rec.start();
    return () => { void rec.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId, mode]);

  // 統一状態 (対話=rt / 自由=rec)。
  const listening = isDialog ? rt.status === "listening" : rec.status === "recording";
  const connecting = isDialog && rt.status === "connecting";
  const paused = isDialog ? rt.paused : rec.paused;
  const error = isDialog ? rt.error : rec.error;
  const turns = rt.turns;
  const activeListening = listening && !paused;
  const stopActive = () => { if (isDialog) void rt.stop(); else void rec.stop(); };

  // REC タイマー (収録中カウント・一時停止中は止める)。
  useEffect(() => {
    if (!activeListening) return;
    const t = setInterval(() => setSec((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [activeListening]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  // 自動終了 (onAutoEnd) と手動の「終了して確認」が競合しても二重抽出しないためのガード。
  const finishedRef = useRef(false);
  const finish = async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    if (isDialog) {
      // 未確定の文字起こし (最後の一言) を待ってから切断する。待ちの間もボタンは処理中表示にする。
      setExtracting(true); setExtractError(null);
      const userText = await rt.stop(true);
      if (!userText.trim()) { setExtracting(false); onBack(); return; }
      // 抽出には AI の質問も含めた対話全体を渡す (「その他」の内容等、質問文脈がないと紐づかない)。
      // stop() の待ち中に確定したターンも拾うため state ではなく最新の ref を読む。
      const finalTurns = rt.getTurns();
      const text = finalTurns.length
        ? finalTurns.map((t) => `${t.role === "assistant" ? "AI" : "回答者"}: ${t.text}`).join("\n")
        : userText;
      try {
        const r = await surveyApi.extract(surveyId, text);
        // ⑤ ツールで確定済みだが抽出に漏れた回答を補完 (ツール=確実性の backstop、抽出=文脈精度の主)。
        const have = new Set(r.answers.map((a) => a.questionId));
        const extras: Answer[] = [...answeredRef.current.entries()]
          .filter(([qid]) => !have.has(qid))
          .map(([questionId, value]) => ({ questionId, value }));
        if (mountedRef.current) onExtracted([...r.answers, ...extras]);
      } catch (e) {
        setExtractError(String(e));
        setExtracting(false);
        finishedRef.current = false; // 失敗時は再試行を許可
      }
      return;
    }
    // 自由モード: 録音停止 → Whisper 文字起こし → 抽出。
    // 収録音声は pendingAudioRef に保持し、失敗時は同じ音声から再試行する (録音を失わない)。
    setExtracting(true); setExtractError(null);
    try {
      let audio = pendingAudioRef.current;
      if (!audio) {
        audio = await rec.stop();
        pendingAudioRef.current = audio;
      }
      if (!audio) { setExtracting(false); onBack(); return; }
      const t = await surveyApi.transcribe(surveyId, audio.base64, audio.mimeType);
      if (!t.text.trim()) {
        setExtractError("音声を認識できませんでした。もう一度お試しください。");
        setExtracting(false);
        finishedRef.current = false; // 再試行を許可
        return;
      }
      const r = await surveyApi.extract(surveyId, t.text);
      pendingAudioRef.current = null;
      if (mountedRef.current) onExtracted(r.answers);
    } catch (e) {
      setExtractError(String(e));
      setExtracting(false);
      finishedRef.current = false; // 失敗時は再試行を許可
    }
  };

  const recLabel = paused ? "一時停止" : connecting ? "接続中…" : error ? "エラー" : listening ? "REC" : "停止しました";
  const pauseDisabled = !listening;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: mode === "freerec" ? "#DCEFEC" : T.bg }}>
      {/* ヘッダー */}
      <div style={{ background: "#fff", padding: "8px 12px 11px", borderBottom: `1px solid ${T.line}`, flex: "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 10 }}>
          <button onClick={() => { stopActive(); onBack(); }} disabled={extracting} style={{ width: 34, height: 34, borderRadius: 9999, background: "transparent", border: 0, cursor: extracting ? "not-allowed" : "pointer", opacity: extracting ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><ArrowLeft size={20} color={T.ink} /></button>
          <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{survey.title}</div>
          <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 6, background: hexA(T.accent, 0.1), borderRadius: 9999, padding: "5px 11px", fontSize: 12, fontWeight: 700, color: T.accent }}>
            <span style={{ width: 8, height: 8, borderRadius: 9999, background: activeListening ? "#EF5A5A" : "#94A3B8", animation: activeListening ? "uqBlink 1.1s infinite" : "none" }} />
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{recLabel} {fmt(sec)}</span>
          </div>
        </div>
        <CustomerContext customer={customer} />
      </div>

      {mode === "freerec" ? (
        // ── 自由モード: ガイド + 波形 (停止後に Whisper 文字起こし) ──
        <>
          <div style={{ flex: "none", padding: "14px 16px 6px", textAlign: "center" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
              <div style={{ width: 46, height: 46, borderRadius: 14, background: "#fff", boxShadow: "0 4px 12px -3px rgba(0,0,0,.12)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
                <Mic size={22} color={T.accent} />
              </div>
              <Waveform active={activeListening} width={92} height={34} />
            </div>
            <div style={{ marginTop: 10, fontSize: 13.5, color: "#4E6285", lineHeight: "20px" }}>下記の項目を参考に<br />記録したいことを自由に話してください</div>
          </div>
          <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "2px 16px 10px", display: "flex", flexDirection: "column", gap: 10 }}>
            {guideItems(survey).map((it, i) => (
              <div key={it.id} style={{ background: "#fff", borderRadius: 14, padding: "13px 15px", boxShadow: "0 1px 3px rgba(0,0,0,.05)", display: "flex", gap: 12 }}>
                <div style={{ width: 26, height: 26, borderRadius: 9999, flex: "none", marginTop: 1, background: hexA(T.accent, 0.12), color: T.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{it.label}</div>
                  <div style={{ marginTop: 3, display: "flex", flexWrap: "wrap", gap: "2px 10px" }}>
                    {it.points.filter(Boolean).map((pt, j) => <span key={j} style={{ fontSize: 12.5, color: T.faint, lineHeight: "18px" }}>・{pt}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        // ── 対話モード: チャット (AI 左 / ユーザー 右) ──
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
          {turns.length === 0 && connecting && (
            <div style={{ textAlign: "center", color: T.faint, fontSize: 13, paddingTop: 24 }}>AI に接続しています…</div>
          )}
          {turns.map((t, i) => t.role === "assistant" ? (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <div style={{ width: 28, height: 28, borderRadius: 9, background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.08)", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}><Sparkles size={14} color={T.accent} /></div>
              <div style={{ maxWidth: "82%", background: "#fff", border: "1px solid #EAF0F7", borderRadius: "4px 16px 16px 16px", padding: "10px 13px", fontSize: 14, lineHeight: "20px", color: T.ink, boxShadow: "0 1px 3px rgba(0,0,0,.04)" }}>{t.text || "…"}</div>
            </div>
          ) : (
            <div key={i} style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ maxWidth: "82%", background: T.accent, color: "#fff", borderRadius: "16px 4px 16px 16px", padding: "10px 14px", fontSize: 14, lineHeight: "20px" }}>{t.text}</div>
            </div>
          ))}
          {activeListening && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ background: "#fff", border: `1.5px solid ${T.accent}`, borderRadius: "16px 4px 16px 16px", padding: "8px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                <Waveform active width={56} height={20} bars={12} />
                <span style={{ fontSize: 12, color: T.accent, fontWeight: 600, whiteSpace: "nowrap" }}>聞き取り中…</span>
              </div>
            </div>
          )}
          {paused && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ background: "#FDECEC", border: "1.5px solid #F3B4B4", borderRadius: "16px 4px 16px 16px", padding: "8px 14px", fontSize: 12, color: "#C0392B", fontWeight: 600 }}>一時停止中</div>
            </div>
          )}
        </div>
      )}

      {(error || extractError) && <div style={{ background: "#FEF2F2", color: "#B91C1C", fontSize: 12, padding: "6px 12px" }}>{error || extractError}</div>}

      <div style={{ background: "#fff", borderTop: `1px solid ${T.line}`, padding: "12px 16px 16px", flex: "none" }}>
        {mode === "dialog" && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 10, fontSize: 12, color: "#4E6285" }}>
            <Sparkles size={13} color={T.accent} /><span style={{ whiteSpace: "nowrap" }}>AI の質問に答えると各項目へ整理されます</span>
          </div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={paused ? (isDialog ? rt.resume : rec.resume) : (isDialog ? rt.pause : rec.pause)} disabled={pauseDisabled}
            aria-label={paused ? "再開" : "一時停止"} title={paused ? "再開" : "一時停止"}
            style={{ flex: "none", width: 54, height: 54, borderRadius: 9999, background: pauseDisabled ? "#F1D5D5" : "#EF5A5A", color: "#fff", border: 0, cursor: pauseDisabled ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {paused ? <Play size={20} color="#fff" fill="#fff" /> : <Pause size={20} color="#fff" fill="#fff" />}
          </button>
          <button onClick={finish} disabled={extracting || connecting}
            style={{ flex: 1, height: 54, borderRadius: 9999, background: extracting || connecting ? "#D8E0EA" : T.accent, color: "#fff", border: 0, fontSize: 15.5, fontWeight: 700, cursor: extracting ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {extracting
              ? <><Sparkles size={18} color="#fff" />AIが整理中…</>
              : !isDialog && extractError
                ? <><Mic size={16} color="#fff" />もう一度試す</>
                : <><Square size={16} color="#fff" fill="#fff" />{mode === "dialog" ? "終了して確認" : "記録を終了する"}</>}
          </button>
        </div>
        {mode === "freerec" && !extracting && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 10, fontSize: 12, color: "#4E6285" }}><Check size={13} color={T.accent} /><span style={{ whiteSpace: "nowrap" }}>終了時にAIが内容を各項目へ自動入力します</span></div>}
      </div>
    </div>
  );
}
